/**
 * TimberCloud brand tokens — v5.2 (Jade / Monochrome)
 *
 * Source of truth for color values referenced in:
 *   - TimberCloud_Brand_Basics_v5_2_Spec.pdf (Brand Basics v5.2, April 2026)
 *   - REBRAND_PLAN_v5.2.md (root of repo)
 *
 * Usage:
 *   - Prefer Tailwind utilities (`bg-jade`, `text-jade-deep`, `border-line`, etc.)
 *     which are wired to `--tc-*` CSS custom properties in `app/src/css/brand-tokens.css`.
 *   - Import these constants only when you need the raw hex in JS (MUI theme,
 *     inline styles, canvas/SVG, chart libs, etc.).
 *   - Do NOT hardcode these hex values elsewhere. Add a new token here first.
 *
 * JADE FOREGROUND vs BACKGROUND RULE (dark mode):
 *   - Background/fill (pill, icon bg, active nav tint): use JADE (#00A86B)
 *   - Foreground text (H3, links, KPI delta, inline bold): use JADE_SOFT (#5BC79A)
 *   - Light mode does NOT need this split — JADE_DEEP handles both roles.
 */

// Jade accent — the primary brand color.
export const JADE = '#00A86B';
export const JADE_SOFT = '#5BC79A';    // Dark-mode jade as foreground text (7.8:1 on obsidian)
export const JADE_DEEP = '#006B45';    // Light-mode all-roles jade (dark UIs use JADE / JADE_SOFT)
export const JADE_TEXT = '#006B45';     // Dark jade for readable light-mode text
export const JADE_CTA = '#34E090';
export const JADE_CTA_DEEP = '#005838'; // Light-mode primary button fill (dark uses JADE_CTA)

// Jade opacity shades — use for depth/layering. Values are rgba strings.
// In Tailwind, prefer `bg-jade-100` etc. Use these for MUI/inline/canvas only.
export const JADE_50  = 'rgba(0, 168, 107, 0.05)';
export const JADE_100 = 'rgba(0, 168, 107, 0.10)';
export const JADE_200 = 'rgba(0, 168, 107, 0.15)';
export const JADE_300 = 'rgba(0, 168, 107, 0.22)';
export const JADE_400 = 'rgba(0, 168, 107, 0.32)';
export const JADE_500 = 'rgba(0, 168, 107, 0.50)';
export const JADE_600 = 'rgba(0, 168, 107, 0.65)';
export const JADE_700 = 'rgba(0, 168, 107, 0.80)';

// Dark-mode neutrals
export const OBSIDIAN = '#111214';
export const ONYX = '#1A1C1E';
export const GRAPHITE = '#202224';
export const SURFACE = '#262829';
export const LINE = '#383A3C';        // Dark-mode hairline. The `line` Tailwind
                                      // token is MODE-AWARE: light renders
                                      // #C5CBC8, since a #383A3C rule on a white
                                      // sheet reads as a near-black box edge.
export const MUTED = '#8C9098';       // Dark-mode secondary text. The `muted`
                                      // Tailwind token is MODE-AWARE too: light
                                      // renders MUTED_COOL #5E6864 (#8C9098 is
                                      // 3.0:1 on white — it fails AA for body).
                                      // Both flips live in brand-tokens.css.
export const SLATE = '#5C6068';        // Light-mode active neutral
export const SLATE_DEEP = '#3A3E44';   // Dark-mode active neutral
export const TEXT_LIGHT = '#EAECEE';

// Light-mode neutrals
export const PEARL = '#F5F8F6';
export const WHITE = '#FFFFFF';
export const LIGHT_BORDER = '#DEE4E0';
export const TEXT_DARK = '#151718';
export const MUTED_COOL = '#5E6864';

// Semantic — status only, never decorative.
// Success IS the brand jade — when something succeeds, the brand celebrates it.
// Info is a distinct blue (NOT the jade primary — green ≠ blue).
export const SUCCESS = { dark: '#00A86B', light: '#006B45' };
export const WARNING = { dark: '#E8C77A', light: '#8A6B1F' };
export const ERROR = { dark: '#D64545', light: '#A02020' };
export const INFO = { dark: '#5B9BD5', light: '#2E6EA8' };
// AI affordances only (mirrors --tc-ai): quiet indigo, distinct from jade actions.
export const AI = { dark: '#818CF8', light: '#4F46E5' };

// Pairing helpers — see §07–08 of Brand Basics v5.2
export const COMPLETED_CALLOUT = {
  dark: {
    bg: '#0D1A14',
    border: JADE,
    text: JADE_SOFT,
  },
  light: {
    bg: '#E5F5ED',
    border: JADE_DEEP,
    text: JADE_DEEP,
  },
};

export const IN_PRODUCTION_CALLOUT = {
  dark: {
    bg: SURFACE,
    border: SLATE_DEEP,
    text: TEXT_LIGHT,
  },
  light: {
    bg: PEARL,
    border: SLATE,
    text: TEXT_DARK,
  },
};

export const ACTIVE_NAV_TINT = {
  dark: {
    text: JADE_SOFT,
    bg: '#0D1A14',
  },
  light: {
    text: JADE_DEEP,
    bg: '#E5F5ED',
  },
};
